import { LitElement, html, css } from 'lit';

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function validateFile(file, accept, maxSize) {
  if (maxSize) {
    let limit = maxSize;
    if (typeof maxSize === 'string') {
      const match = maxSize.match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)?$/i);
      if (match) {
        const value = parseFloat(match[1]);
        const unit = (match[2] || '').toUpperCase();
        if (unit === 'KB') limit = value * 1024;
        else if (unit === 'MB') limit = value * 1024 * 1024;
        else if (unit === 'GB') limit = value * 1024 * 1024 * 1024;
        else limit = value;
      }
    }
    if (file.size > limit) {
      return `Le fichier dépasse la taille autorisée.`;
    }
  }

  if (accept) {
    const rules = accept.split(',').map(r => r.trim().toLowerCase());
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    
    const isAccepted = rules.some(rule => {
      if (rule.startsWith('.')) {
        return fileName.endsWith(rule);
      } else if (rule.endsWith('/*')) {
        const category = rule.replace('/*', '');
        return fileType.startsWith(category);
      } else {
        return fileType === rule;
      }
    });
    
    if (!isAccepted) {
      return `Type de fichier non autorisé.`;
    }
  }

  return null;
}

export class UIUpload extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    accept: { type: String },
    maxSize: { type: String }, // e.g. "5MB", "500KB"
    multiple: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    progress: { type: Number }, // 0 to 100
    label: { type: String, reflect: true },

    // Internal States
    isDragOver: { type: Boolean, state: true },
    filesList: { type: Array, state: true },
    errors: { type: Array, state: true }
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .upload-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* Dropzone Area */
    .upload-dropzone {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 32px 24px;
      border: 2px dashed var(--ui-border);
      border-radius: var(--ui-radius-lg);
      background-color: var(--ui-surface);
      cursor: pointer;
      text-align: center;
      transition: border-color var(--ui-transition-normal), 
                  background-color var(--ui-transition-normal), 
                  box-shadow var(--ui-transition-normal);
      outline: none;
    }

    .upload-dropzone:hover:not(.disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .upload-dropzone:focus-within:not(.disabled) {
      border-color: var(--ui-primary);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .upload-dropzone.dragover:not(.disabled) {
      border-color: var(--ui-primary);
      background-color: color-mix(in srgb, var(--ui-primary) 5%, var(--ui-surface));
      border-style: solid;
      animation: dash-pulse 1.5s infinite linear;
    }

    @keyframes dash-pulse {
      0% {
        box-shadow: 0 0 0 0 var(--ui-focus-ring);
      }
      100% {
        box-shadow: 0 0 0 8px transparent;
      }
    }

    .upload-dropzone.disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    /* Hidden Native File Input */
    .file-input-hidden {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-input-hidden:disabled {
      cursor: not-allowed;
    }

    /* Content / Info */
    .upload-icon {
      font-size: 32px;
      color: var(--ui-text-light);
      margin-bottom: 12px;
      transition: color var(--ui-transition-fast), transform var(--ui-transition-fast);
    }

    .upload-dropzone:hover .upload-icon:not(.disabled) {
      color: var(--ui-primary);
      transform: translateY(-2px);
    }

    .upload-text {
      font-weight: 500;
      margin-bottom: 4px;
      color: var(--ui-text);
    }

    .upload-text span {
      color: var(--ui-primary);
      font-weight: 600;
    }

    .upload-hint {
      font-size: var(--ui-font-size-sm);
      color: var(--ui-text-muted);
    }

    /* Errors list */
    .upload-errors {
      color: var(--ui-danger);
      font-size: var(--ui-font-size-sm);
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    /* Progress bar */
    .progress-bar-container {
      width: 100%;
      height: 6px;
      background-color: var(--ui-surface-active);
      border-radius: var(--ui-radius-full);
      overflow: hidden;
      margin-top: 4px;
    }

    .progress-bar-fill {
      height: 100%;
      background-color: var(--ui-primary);
      transition: width 200ms ease;
    }

    /* File List Chips/Cards */
    .files-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 4px;
    }

    .file-card {
      display: flex;
      align-items: center;
      padding: 10px 14px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      background-color: var(--ui-surface);
      gap: 12px;
      transition: var(--ui-transition-fast);
      box-shadow: var(--ui-shadow-sm);
    }

    .file-card:hover {
      border-color: var(--ui-border-hover);
      box-shadow: var(--ui-shadow-md);
    }

    /* Preview Thumbnail */
    .file-thumbnail {
      width: 40px;
      height: 40px;
      border-radius: var(--ui-radius-sm);
      border: 1px solid var(--ui-border);
      object-fit: cover;
      flex-shrink: 0;
      background-color: var(--ui-surface-active);
    }

    .file-icon-fallback {
      width: 40px;
      height: 40px;
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-surface-active);
      border: 1px solid var(--ui-border);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--ui-text-muted);
      font-size: var(--ui-font-size-lg);
      font-weight: 700;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .file-details {
      flex: 1;
      min-width: 0;
    }

    .file-name {
      font-weight: 500;
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
    }

    .file-size {
      font-size: var(--ui-font-size-sm);
      color: var(--ui-text-muted);
    }

    .btn-remove-file {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: var(--ui-radius-full);
      border: none;
      background-color: transparent;
      color: var(--ui-text-muted);
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-remove-file:hover {
      background-color: var(--ui-surface-active);
      color: var(--ui-danger);
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.accept = '';
    this.maxSize = '';
    this.multiple = false;
    this.disabled = false;
    this.required = false;
    this.progress = 0;
    this.label = '';

    // Internal arrays
    this.isDragOver = false;
    this.filesList = [];
    this.errors = [];
    this._objectUrls = new Map();
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  // Expose current files array
  get files() {
    return this.filesList;
  }

  updated(changedProperties) {
    if (changedProperties.has('filesList') || changedProperties.has('required') || changedProperties.has('name')) {
      this._updateFormValue();
      this._updateValidity();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up created object URLs
    this._objectUrls.forEach(url => URL.revokeObjectURL(url));
    this._objectUrls.clear();
  }

  _updateFormValue() {
    if (this.filesList.length === 0) {
      this.internals.setFormValue(null);
    } else {
      const fd = new FormData();
      const inputName = this.name || 'files';
      this.filesList.forEach(file => {
        fd.append(inputName, file);
      });
      this.internals.setFormValue(fd);
    }
  }

  _updateValidity() {
    const hasFiles = this.filesList.length > 0;
    if (this.required && !hasFiles) {
      this.internals.setValidity(
        { valueMissing: true }, 
        'Veuillez sélectionner au moins un fichier.',
        this.shadowRoot.querySelector('.upload-dropzone')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  _getThumbnail(file) {
    if (file.type.startsWith('image/')) {
      if (!this._objectUrls.has(file)) {
        const url = URL.createObjectURL(file);
        this._objectUrls.set(file, url);
      }
      return this._objectUrls.get(file);
    }
    return null;
  }

  _getFileExtension(file) {
    const parts = file.name.split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  _handleDragOver(e) {
    e.preventDefault();
    if (this.disabled) return;
    this.isDragOver = true;
  }

  _handleDragLeave(e) {
    e.preventDefault();
    this.isDragOver = false;
  }

  _handleDrop(e) {
    e.preventDefault();
    if (this.disabled) return;
    this.isDragOver = false;

    const files = Array.from(e.dataTransfer.files);
    this._processFiles(files);
  }

  _handleFileChange(e) {
    const files = Array.from(e.target.files);
    this._processFiles(files);
    // Reset file input so same file can be chosen again if removed
    e.target.value = '';
  }

  _processFiles(incomingFiles) {
    if (this.disabled) return;
    this.errors = [];

    let validated = [];
    
    incomingFiles.forEach(file => {
      const err = validateFile(file, this.accept, this.maxSize);
      if (err) {
        this.errors = [...this.errors, `${file.name}: ${err}`];
      } else {
        validated.push(file);
      }
    });

    if (validated.length === 0) return;

    if (this.multiple) {
      this.filesList = [...this.filesList, ...validated];
    } else {
      // If single selection, revoke previous URLs
      this._objectUrls.forEach(url => URL.revokeObjectURL(url));
      this._objectUrls.clear();
      this.filesList = [validated[0]];
    }

    this._dispatchChangeEvent();
  }

  _removeFile(index, e) {
    e.stopPropagation();
    if (this.disabled) return;
    
    const fileToRemove = this.filesList[index];
    if (this._objectUrls.has(fileToRemove)) {
      URL.revokeObjectURL(this._objectUrls.get(fileToRemove));
      this._objectUrls.delete(fileToRemove);
    }

    this.filesList = this.filesList.filter((_, i) => i !== index);
    this._dispatchChangeEvent();
  }

  _dispatchChangeEvent() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { files: this.filesList },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="upload-container">
        <!-- Drag zone wrapper -->
        <div 
          part="input" class="upload-dropzone ${this.isDragOver ? 'dragover' : ''} ${this.disabled ? 'disabled' : ''}"
          @dragover="${this._handleDragOver}"
          @dragleave="${this._handleDragLeave}"
          @drop="${this._handleDrop}"
        >
          <input 
            type="file"
            class="file-input-hidden"
            ?multiple="${this.multiple}"
            ?disabled="${this.disabled}"
            accept="${this.accept || ''}"
            @change="${this._handleFileChange}"
          />
          
          <div class="upload-icon ${this.disabled ? 'disabled' : ''}">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </div>
          
          <div class="upload-text">
            <span>Glissez-déposez</span> vos fichiers ou cliquez pour parcourir
          </div>
          
          <div class="upload-hint">
            ${this.maxSize ? `Taille max : ${this.maxSize}.` : ''} 
            ${this.accept ? `Formats acceptés : ${this.accept}` : 'Tous formats autorisés.'}
          </div>
        </div>

        <!-- Global Upload progress bar -->
        ${this.progress > 0 && this.progress < 100 ? html`
          <div>
            <div class="upload-hint" style="display: flex; justify-content: space-between; margin-bottom: 2px;">
              <span>Téléversement en cours...</span>
              <span>${Math.round(this.progress)}%</span>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar-fill" style="width: ${this.progress}%"></div>
            </div>
          </div>
        ` : ''}

        <!-- Errors display -->
        ${this.errors.length > 0 ? html`
          <div class="upload-errors">
            ${this.errors.map(err => html`<div>⚠️ ${err}</div>`)}
          </div>
        ` : ''}

        <!-- Files selected preview list -->
        ${this.filesList.length > 0 ? html`
          <div class="files-list">
            ${this.filesList.map((file, idx) => {
              const thumb = this._getThumbnail(file);
              return html`
                <div class="file-card">
                  ${thumb ? html`
                    <img class="file-thumbnail" src="${thumb}" alt="thumbnail" />
                  ` : html`
                    <div class="file-icon-fallback">
                      ${this._getFileExtension(file) || '?'}
                    </div>
                  `}
                  
                  <div class="file-details">
                    <div class="file-name" title="${file.name}">${file.name}</div>
                    <div class="file-size">${formatFileSize(file.size)}</div>
                  </div>
                  
                  <button 
                    type="button" 
                    class="btn-remove-file"
                    ?disabled="${this.disabled}"
                    @click="${(e) => this._removeFile(idx, e)}"
                    title="Supprimer le fichier"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              `;
            })}
          </div>
        ` : ''}
      </div>
    `;
  }
}

if (!customElements.get('ui-upload')) {
  customElements.define('ui-upload', UIUpload);
}
